import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { StatCard, Button, Table, Alert, Spinner, DateRangePicker, type TableColumn } from '@/ui'
import { formatCurrency, formatNumber } from '@/ui'
import { resolveRange, SHARED_RANGE_PRESETS } from '@/lib/dateRange'
import { RefreshCw } from 'lucide-react'
import styles from './Settlements.module.css'

interface Settlement {
  settleExpectDate: string
  settleCompleteDate: string | null
  settleBasisStartDate: string | null
  settleBasisEndDate: string | null
  settleAmount: number
  paySettleAmount: number
  commissionSettleAmount: number
  benefitSettleAmount: number
  normalSettleAmount: number
  quickSettleAmount: number
  deductionRestoreSettleAmount: number
  payHoldbackAmount: number
  minusChargeAmount: number
  returnCareSettleAmount: number
  preferentialCommissionAmount: number
  settleMethodType: string | null
}

interface SettlementsResponse {
  settlements: Settlement[]
  summary: {
    settleAmount: number
    paySettleAmount: number
    commissionSettleAmount: number
    benefitSettleAmount: number
    count: number
  }
}

interface SyncResult {
  synced: number
  dateBackfilled: number
  unmatchedOrders: number
  backfillError: string | null
}

// 빠른검색 프리셋 — admin 공용(SHARED_RANGE_PRESETS). 기간 해석은 공용 resolveRange(기본 최근 30일).

export function Settlements() {
  const [sp, setSp] = useSearchParams()
  const { from, to, preset } = resolveRange(sp, '30d')

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k)
      else next.set(k, v)
    }
    setSp(next)
  }

  const fetchUrl = `/api/admin/smartstore/settlements?from=${from}&to=${to}`
  const { data, isLoading, error, refetch } = useApiFetch<SettlementsResponse>(fetchUrl)

  // 검색 버튼 로딩 — 요청 URL이 바뀌면 응답 도착까지 searching
  const [searching, setSearching] = useState(false)
  const loadedUrlRef = useRef(fetchUrl)
  useEffect(() => {
    if (loadedUrlRef.current !== fetchUrl) setSearching(true)
  }, [fetchUrl])
  useEffect(() => {
    loadedUrlRef.current = fetchUrl
    setSearching(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error])

  // 정산 동기화
  const { addToast } = useToast()
  const { trigger: triggerSync, isLoading: syncing } = useApiMutation<SyncResult, { from: string; to: string }>(
    '/api/admin/smartstore/settlements/sync',
    'POST',
  )

  const handleSync = async () => {
    const result = await triggerSync({ from, to })
    if (result) {
      addToast({
        type: 'success',
        message: `정산 동기화 완료 — ${result.synced}건 · 정산일 백필 ${result.dateBackfilled}건${result.unmatchedOrders > 0 ? ` · 미매칭 ${result.unmatchedOrders}건` : ''}`,
      })
      if (result.backfillError) {
        addToast({ type: 'error', message: `정산일 백필 실패: ${result.backfillError}` })
      }
      refetch()
    } else {
      addToast({ type: 'error', message: '정산 동기화에 실패했습니다' })
    }
  }

  // 기간 선택 — 프리셋이면 preset 키, 커스텀 범위면 from/to 를 URL 에 커밋.
  const handleRangeChange = (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    if (meta.presetKey) {
      updateParams({ preset: meta.presetKey, from: null, to: null })
    } else if (range.from && range.to) {
      updateParams({ from: range.from, to: range.to, preset: null })
    }
  }

  const won = (n: number) => formatCurrency(n)

  const columns: TableColumn<Settlement>[] = [
    { key: 'settleExpectDate', label: '정산예정일', render: (r) => r.settleExpectDate, width: '110px' },
    { key: 'settleCompleteDate', label: '정산완료일', render: (r) => r.settleCompleteDate ?? '-', width: '110px' },
    { key: 'settleBasis', label: '정산기준기간', render: (r) => r.settleBasisStartDate ? `${r.settleBasisStartDate} ~ ${r.settleBasisEndDate ?? ''}` : '-', width: '180px' },
    { key: 'settleAmount', label: '정산금액', render: (r) => won(r.settleAmount), align: 'right' as const, width: '110px' },
    { key: 'paySettleAmount', label: '정산기준금액', render: (r) => won(r.paySettleAmount), align: 'right' as const, width: '120px' },
    { key: 'commissionSettleAmount', label: '수수료합계', render: (r) => won(r.commissionSettleAmount), align: 'right' as const, width: '110px' },
    { key: 'benefitSettleAmount', label: '혜택정산', render: (r) => won(r.benefitSettleAmount), align: 'right' as const, width: '100px' },
    { key: 'normalSettleAmount', label: '일반정산', render: (r) => won(r.normalSettleAmount), align: 'right' as const, width: '100px' },
    { key: 'quickSettleAmount', label: '빠른정산', render: (r) => won(r.quickSettleAmount), align: 'right' as const, width: '100px' },
    { key: 'payHoldbackAmount', label: '지급보류', render: (r) => won(r.payHoldbackAmount), align: 'right' as const, width: '100px' },
    { key: 'returnCareSettleAmount', label: '반품안심케어', render: (r) => won(r.returnCareSettleAmount), align: 'right' as const, width: '110px' },
    { key: 'settleMethodType', label: '정산방식', render: (r) => r.settleMethodType ?? '-', width: '90px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.hint}>네이버 스마트스토어 정산 내역 (날짜별)</span>
        <Button size="sm" variant="secondary" loading={syncing} onClick={handleSync}>
          <RefreshCw size={14} />
          정산 동기화
        </Button>
      </div>

      <div className={styles.statGrid}>
        <StatCard label="정산금액 합" value={won(data?.summary.settleAmount ?? 0)} />
        <StatCard label="정산기준금액 합" value={won(data?.summary.paySettleAmount ?? 0)} />
        <StatCard label="수수료 합" value={won(data?.summary.commissionSettleAmount ?? 0)} />
        <StatCard label="혜택정산 합" value={won(data?.summary.benefitSettleAmount ?? 0)} />
        <StatCard label="정산 건수" value={`${formatNumber(data?.summary.count ?? 0)}건`} />
      </div>

      <div className={styles.filters}>
        <div className={styles.customRange}>
          <DateRangePicker
            size="sm"
            value={{ from, to }}
            activePresetKey={preset}
            presets={SHARED_RANGE_PRESETS}
            onChange={handleRangeChange}
          />
          {searching && <Spinner size="sm" />}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<Settlement>
        columns={columns}
        data={data?.settlements ?? []}
        rowKey={(r) => `${r.settleExpectDate}_${r.settleMethodType ?? ''}`}
        isLoading={isLoading}
      />
    </div>
  )
}
